import type { AuthUser } from '../types/auth'

/**
 * Chave do filtro global de empresa (CompanyFilterContext).
 * Definida aqui — e não no contexto — para o purge não importar do contexto
 * (que importa AuthContext, que importa este arquivo: ciclo).
 *
 * A chave MUDOU junto com o eixo do seletor (CC → empresa, 29/09/2026). O
 * valor guardado é uma lista de ids: manter o nome antigo faria um id de
 * CENTRO DE CUSTO persistido ser lido como id de EMPRESA e filtrar o painel
 * pela empresa errada, em silêncio. Chave nova = seleção antiga inválida por
 * construção (o seletor volta a "todas as empresas").
 */
export const COMPANY_FILTER_STORAGE_KEY = 'global_company_filter'

/** Chave do seletor por CC, morta em 29/09/2026 — fica só para ser purgada. */
const LEGACY_COST_CENTER_FILTER_STORAGE_KEY = 'global_cost_center_filter'

export const PAGE_STATE_OWNER_KEY = 'pageState_owner'

// Estado de página persistido (filtros/paginação/views do painel aduaneiro, do
// Pós-Registro e da spreadsheet). Escopo do purge na troca de usuário: prefixos + chaves soltas
// usadas fora do padrão de prefixo.
const PAGE_STATE_PREFIXES = ['customsPanel_', 'spreadsheet_', 'poPanel_', 'postRegistry_']
const PAGE_STATE_KEYS = [
  'was_in_spreadsheet',
  'should_open_spreadsheet',
  COMPANY_FILTER_STORAGE_KEY,
  LEGACY_COST_CENTER_FILTER_STORAGE_KEY,
]

/**
 * Identidade efetiva dona do estado de página. Os ids vêm de uma tabela só
 * (usuário único); inclui a empresa do modo "ver como" — trocar de modo
 * também muda o escopo das queries.
 */
export function pageStateIdentity(user: AuthUser): string {
  return `${user.id}:${user.view_as_company_id ?? ''}`
}

function clearPageState(): void {
  const doomed: string[] = []
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (!key) continue
    if (PAGE_STATE_PREFIXES.some((p) => key.startsWith(p)) || PAGE_STATE_KEYS.includes(key)) {
      doomed.push(key)
    }
  }
  doomed.forEach((key) => localStorage.removeItem(key))
}

/**
 * Garante que o estado de página persistido pertence à identidade autenticada.
 * Dono ausente (primeiro uso) adota sem purgar; identidade diferente purga o
 * estado ANTES de qualquer hidratação — os hooks do painel/spreadsheet leem o
 * localStorage em inicializadores de useState, então isso precisa rodar de
 * forma síncrona antes do setUser que monta essas telas.
 */
export function ensurePageStateOwner(identity: string): void {
  try {
    const owner = localStorage.getItem(PAGE_STATE_OWNER_KEY)
    if (owner === identity) return
    if (owner !== null) clearPageState()
    localStorage.setItem(PAGE_STATE_OWNER_KEY, identity)
  } catch {
    /* storage indisponível — nada a purgar */
  }
}
